// Copyright (c) 2026 Goffy. This file belongs to Goffy. All Rights Reserved.

import { ResizeMode, Video, type AVPlaybackStatus } from 'expo-av';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  ActivityIndicator,
  Image,
  Platform,
  Pressable,
  StyleSheet,
  Text,
  View,
  type GestureResponderEvent,
} from 'react-native';

import { HoleMapCanvas, type MapOverlayPolygon } from '@/features/hole/holeview/HoleMapCanvas';
import { OVERLAY_COLORS } from '@/features/hole/holeview/holeShapes';
import { buildTileUrl, computeLayoutAt } from '@/features/hole/holeview/tileMath';
import { getSatelliteTileUrlTemplate } from '@/features/hole/holeview/tileSource';
import { formatHoleDistance } from '@/features/units/distance';
import { useI18n } from '@/i18n';
import { useDistanceUnit } from '@/stores/distanceUnitState';
import appColors from '@/theme/colors';

import { buildHoleCameraPath } from './flyoverCamera';
import type { FlyoverHole } from './useCourseFlyover';

export type FlyoverPlayerProps = {
  courseName: string;
  /** Holes worth flying, course order — `useCourseFlyover`'s output. */
  holes: FlyoverHole[];
  loading: boolean;
  unavailable: boolean;
  onClose: () => void;
  /**
   * Server-rendered flyover MP4s per hole, where the offline render script has produced
   * them. A hole with a video plays it; every other hole plays the live 2D flyover.
   */
  videoUrlByHole?: Record<number, string>;
};

/** Roughly the size the canvas renders at; only used to list tiles worth prefetching. */
const PREFETCH_VIEWPORT = { width: 400, height: 400 };

/** m:ss — a flyover hole is well under an hour, so hours would only ever read "0:". */
const formatClock = (millis: number): string => {
  const totalSeconds = Math.max(0, Math.floor(millis / 1000));
  return `${Math.floor(totalSeconds / 60)}:${String(totalSeconds % 60).padStart(2, '0')}`;
};

/**
 * Plays a course hole by hole: the camera lifts off the tee, glides down the line of play
 * and settles on the green, then moves to the next hole.
 *
 * The animation drives `HoleMapCanvas` through its `camera` prop — one
 * `requestAnimationFrame` loop advancing a clock, the camera path turning that clock into
 * a centre. The span and bearing are constant per hole, so the tile zoom level never
 * changes mid-flight and panning reuses the mounted tiles.
 */
export function FlyoverPlayer({
  courseName,
  holes,
  loading,
  unavailable,
  onClose,
  videoUrlByHole,
}: FlyoverPlayerProps) {
  const { t } = useI18n();
  const distanceUnit = useDistanceUnit();
  const [holeIndex, setHoleIndex] = useState(0);
  const [playing, setPlaying] = useState(true);
  const [clockMs, setClockMs] = useState(0);

  const hole = holes[holeIndex] ?? null;
  const videoUrl = hole ? (videoUrlByHole?.[hole.holeNumber] ?? null) : null;
  const path = useMemo(
    () => (hole && hole.green ? buildHoleCameraPath({ tee: hole.tees[0], green: hole.green }) : null),
    [hole]
  );

  // The clock only ever moves relative to when play (re)started, so pausing holds the
  // frame and resuming continues from it rather than snapping back to the tee.
  const playedUpToRef = useRef(0);
  const rafRef = useRef<number | null>(null);

  // Where the stored video is, for the scrub bar. `scrubFraction` is set only while a
  // finger is on the bar: it, not the playback status, is the truth for that stretch.
  const videoRef = useRef<Video>(null);
  const [videoPositionMs, setVideoPositionMs] = useState(0);
  const [videoDurationMs, setVideoDurationMs] = useState(0);
  const [scrubFraction, setScrubFraction] = useState<number | null>(null);
  const trackWidthRef = useRef(0);
  const grantFractionRef = useRef(0);
  const grantPageXRef = useRef(0);
  const seekInFlightRef = useRef(false);
  const pendingSeekRef = useRef<number | null>(null);

  useEffect(() => {
    playedUpToRef.current = 0;
    setClockMs(0);
    setVideoPositionMs(0);
    setVideoDurationMs(0);
    setScrubFraction(null);
  }, [holeIndex, holes]);

  /**
   * Move the picture with the finger, not on release.
   *
   * expo-av serves one seek at a time and a drag asks for far more than it can answer, so
   * a request that arrives mid-seek is held as the single pending one — the finger's latest
   * position wins and the positions it swept past are dropped rather than queued, which is
   * what keeps the video tracking the thumb instead of chasing it.
   */
  const seekVideoTo = useCallback((positionMs: number) => {
    const video = videoRef.current;
    if (!video) return;
    if (seekInFlightRef.current) {
      pendingSeekRef.current = positionMs;
      return;
    }
    seekInFlightRef.current = true;
    const run = (ms: number) => {
      void Promise.resolve(video.setPositionAsync(ms))
        .catch(() => undefined)
        .then(() => {
          const next = pendingSeekRef.current;
          pendingSeekRef.current = null;
          if (next === null) {
            seekInFlightRef.current = false;
            return;
          }
          run(next);
        });
    };
    run(positionMs);
  }, []);

  const scrubToFraction = useCallback(
    (fraction: number) => {
      const clamped = Math.min(1, Math.max(0, fraction));
      setScrubFraction(clamped);
      if (videoDurationMs <= 0) return;
      const positionMs = clamped * videoDurationMs;
      setVideoPositionMs(positionMs);
      seekVideoTo(positionMs);
    },
    [seekVideoTo, videoDurationMs]
  );

  const scrubHandlers = useMemo(
    () => ({
      onStartShouldSetResponder: () => true,
      // A scrub is a horizontal drag inside a screen that scrolls vertically; without this
      // the scroll view claims the gesture partway through and the thumb is left behind.
      onResponderTerminationRequest: () => false,
      onResponderGrant: (event: GestureResponderEvent) => {
        const width = trackWidthRef.current;
        grantPageXRef.current = event.nativeEvent.pageX;
        grantFractionRef.current =
          width > 0 ? Math.min(1, Math.max(0, event.nativeEvent.locationX / width)) : 0;
        scrubToFraction(grantFractionRef.current);
      },
      // Offset from where the finger landed, in page coordinates. `locationX` is measured
      // against whatever element the event is aimed at, which on web stops being the bar as
      // soon as the drag strays off it — and straying off it is the normal way to reach
      // either end. `pageX` is the same origin throughout.
      onResponderMove: (event: GestureResponderEvent) => {
        const width = trackWidthRef.current;
        if (width <= 0) return;
        const travelled = event.nativeEvent.pageX - grantPageXRef.current;
        scrubToFraction(grantFractionRef.current + travelled / width);
      },
      onResponderRelease: () => setScrubFraction(null),
      onResponderTerminate: () => setScrubFraction(null),
    }),
    [scrubToFraction]
  );

  const progress =
    scrubFraction ?? (videoDurationMs > 0 ? Math.min(1, videoPositionMs / videoDurationMs) : 0);

  const advance = useCallback(() => {
    setHoleIndex((index) => {
      if (index + 1 < holes.length) return index + 1;
      // The last green is the end of the show; hold there rather than looping.
      setPlaying(false);
      return index;
    });
  }, [holes.length]);

  useEffect(() => {
    // A stored video drives its own clock; the animation loop only runs the 2D flight.
    if (!playing || !path || videoUrl) return undefined;

    const startedAt = Date.now() - playedUpToRef.current;
    const tick = () => {
      const elapsed = Date.now() - startedAt;
      playedUpToRef.current = elapsed;
      if (elapsed >= path.totalMs) {
        playedUpToRef.current = 0;
        advance();
        return;
      }
      setClockMs(elapsed);
      rafRef.current = requestAnimationFrame(tick);
    };
    rafRef.current = requestAnimationFrame(tick);

    return () => {
      if (rafRef.current !== null) cancelAnimationFrame(rafRef.current);
    };
  }, [advance, path, playing, videoUrl]);

  const camera = useMemo(() => (path ? path.cameraAt(clockMs) : null), [clockMs, path]);

  const polygons = useMemo<MapOverlayPolygon[]>(
    () =>
      (hole?.shapes ?? []).map((shape) => ({
        id: shape.id,
        ring: shape.ring,
        color: OVERLAY_COLORS[shape.kind] ?? '#FFFFFF',
      })),
    [hole]
  );

  // Warms the next hole's tiles while this hole plays, so the cut is not a blank mosaic.
  // Web leaves it to the browser's own cache; Image.prefetch is a native affair.
  useEffect(() => {
    if (Platform.OS === 'web') return;
    const next = holes[holeIndex + 1];
    if (!next?.green) return;
    const nextPath = buildHoleCameraPath({ tee: next.tees[0], green: next.green });
    const template = getSatelliteTileUrlTemplate();
    for (const at of [0, nextPath.durationMs / 2, nextPath.durationMs]) {
      const layout = computeLayoutAt({
        center: nextPath.cameraAt(at).center,
        spanMeters: nextPath.cameraAt(at).spanMeters,
        bearingDegrees: nextPath.cameraAt(at).bearingDegrees,
        ...PREFETCH_VIEWPORT,
      });
      for (const tile of layout?.tiles ?? []) {
        // Best-effort warm-up; the RN test renderer's Image stub returns no promise at all.
        void Image.prefetch(buildTileUrl(template, tile))?.catch?.(() => {});
      }
    }
  }, [holeIndex, holes]);

  const stepTo = useCallback(
    (index: number) => {
      if (index < 0 || index >= holes.length) return;
      setHoleIndex(index);
      setPlaying(true);
    },
    [holes.length]
  );

  const caption = useMemo(() => {
    if (!hole) return '';
    const pieces = [t('tourPreview.holeCaption', { number: hole.holeNumber })];
    if (hole.par !== null) pieces.push(t('tourPreview.parShort', { par: hole.par }));
    if (hole.strokeIndex !== null) {
      pieces.push(t('tourPreview.strokeIndexShort', { strokeIndex: hole.strokeIndex }));
    }
    if (hole.lengthMeters !== null && hole.lengthMeters > 0) {
      pieces.push(formatHoleDistance(hole.lengthMeters, distanceUnit));
    }
    return pieces.join(' · ');
  }, [distanceUnit, hole, t]);

  return (
    <View style={styles.container} testID="flyover-player">
      <View style={styles.header}>
        <Text style={styles.title} numberOfLines={1}>
          {courseName}
        </Text>
        <Pressable
          accessibilityRole="button"
          testID="flyover-close"
          onPress={onClose}
          style={styles.closeButton}
        >
          <Text style={styles.closeText}>✕</Text>
        </Pressable>
      </View>

      {loading ? (
        <View style={styles.stateBox} testID="flyover-loading">
          <ActivityIndicator color={appColors.accent.primary} />
        </View>
      ) : unavailable || !hole || !camera ? (
        <View style={styles.stateBox} testID="flyover-unavailable">
          <Text style={styles.stateText}>{t('tourPreview.courseNotMapped')}</Text>
        </View>
      ) : (
        <>
          {videoUrl ? (
            <>
              <Video
                testID="flyover-video"
                ref={videoRef}
                source={{ uri: videoUrl }}
                style={styles.video}
                // Same defect as the hole view's player: an absolutely positioned <video>
                // with auto width takes its own pixel size, not the box's, so on web the box
                // only crops it. Filling the box is what makes CONTAIN fit the picture.
                videoStyle={styles.videoSurface}
                resizeMode={ResizeMode.CONTAIN}
                shouldPlay={playing}
                // The default half-second between status frames is visible as a stepping
                // progress bar; this is the rate the fill moves at.
                progressUpdateIntervalMillis={100}
                onPlaybackStatusUpdate={(status: AVPlaybackStatus) => {
                  if (!status.isLoaded) return;
                  setVideoDurationMs(status.durationMillis ?? 0);
                  // A finger on the bar owns the position: status frames issued before the
                  // seek landed still describe the old one and would drag the thumb back.
                  if (scrubFraction === null) setVideoPositionMs(status.positionMillis ?? 0);
                  if (status.didJustFinish) advance();
                }}
              />
              <View style={styles.scrubber} testID="flyover-scrubber">
                <Text style={styles.scrubberTime} testID="flyover-elapsed">
                  {formatClock(videoPositionMs)}
                </Text>
                <View
                  style={styles.trackTouchArea}
                  testID="flyover-scrubber-touch"
                  {...scrubHandlers}
                >
                  <View
                    style={styles.track}
                    testID="flyover-scrubber-track"
                    accessibilityRole="adjustable"
                    accessibilityLabel={t('tourPreview.videoPosition')}
                    accessibilityValue={{ min: 0, max: 100, now: Math.round(progress * 100) }}
                    onLayout={(event) => {
                      trackWidthRef.current = event.nativeEvent.layout.width;
                    }}
                  >
                    <View
                      testID="flyover-scrubber-fill"
                      style={[styles.trackFill, { width: `${progress * 100}%` }]}
                    />
                    <View style={[styles.trackThumb, { left: `${progress * 100}%` }]} />
                  </View>
                </View>
                <Text style={styles.scrubberTime} testID="flyover-duration">
                  {formatClock(videoDurationMs)}
                </Text>
              </View>
            </>
          ) : (
            <HoleMapCanvas
              tees={hole.tees}
              green={hole.green}
              loading={false}
              unavailable={false}
              polygons={polygons}
              camera={camera}
            />
          )}
          <Text style={styles.caption} testID="flyover-caption">
            {caption}
          </Text>
          <View style={styles.controls}>
            <Pressable
              accessibilityRole="button"
              testID="flyover-previous"
              disabled={holeIndex === 0}
              onPress={() => stepTo(holeIndex - 1)}
              style={[styles.controlButton, holeIndex === 0 ? styles.controlDisabled : null]}
            >
              <Text style={styles.controlText}>{t('tourPreview.previousHole')}</Text>
            </Pressable>
            <Pressable
              accessibilityRole="button"
              testID="flyover-play-pause"
              onPress={() => setPlaying((value) => !value)}
              style={styles.controlButton}
            >
              <Text style={styles.controlText}>
                {playing ? t('tourPreview.pause') : t('tourPreview.play')}
              </Text>
            </Pressable>
            <Pressable
              accessibilityRole="button"
              testID="flyover-next"
              disabled={holeIndex >= holes.length - 1}
              onPress={() => stepTo(holeIndex + 1)}
              style={[
                styles.controlButton,
                holeIndex >= holes.length - 1 ? styles.controlDisabled : null,
              ]}
            >
              <Text style={styles.controlText}>{t('tourPreview.nextHole')}</Text>
            </Pressable>
          </View>
        </>
      )}
    </View>
  );
}

const styles = StyleSheet.create({
  container: {
    backgroundColor: '#07200D',
    borderRadius: 12,
    padding: 12,
    gap: 10,
  },
  header: { flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between' },
  title: { color: '#FFFFFF', fontSize: 16, fontWeight: '700', flexShrink: 1 },
  closeButton: { padding: 6 },
  closeText: { color: '#FFFFFF', fontSize: 16 },
  stateBox: {
    height: 220,
    alignItems: 'center',
    justifyContent: 'center',
  },
  stateText: { color: appColors.text.primary, fontSize: 14, textAlign: 'center' },
  video: { width: '100%', aspectRatio: 16 / 9, borderRadius: 10, backgroundColor: '#000000' },
  videoSurface: { width: '100%', height: '100%' },
  scrubber: { flexDirection: 'row', alignItems: 'center', gap: 8 },
  scrubberTime: {
    color: '#FFFFFF',
    fontSize: 12,
    fontVariant: ['tabular-nums'],
    // Both ends keep the bar still as the digits change; m:ss up to 9:59 fits.
    minWidth: 34,
    textAlign: 'center',
  },
  // The bar is 4px of picture but a 24px target: a finger, not a mouse, is the usual
  // pointer here. No horizontal padding — locationX is read against this view, so its left
  // edge has to be the track's.
  trackTouchArea: { flex: 1, paddingVertical: 10, justifyContent: 'center' },
  track: { height: 4, borderRadius: 2, backgroundColor: 'rgba(255,255,255,0.25)' },
  trackFill: { height: 4, borderRadius: 2, backgroundColor: appColors.accent.primary },
  trackThumb: {
    position: 'absolute',
    top: -4,
    width: 12,
    height: 12,
    borderRadius: 6,
    marginLeft: -6,
    backgroundColor: '#FFFFFF',
  },
  caption: {
    color: '#FFFFFF',
    fontSize: 14,
    fontWeight: '600',
    textAlign: 'center',
    fontVariant: ['tabular-nums'],
  },
  controls: { flexDirection: 'row', justifyContent: 'center', gap: 10 },
  controlButton: {
    paddingVertical: 8,
    paddingHorizontal: 14,
    borderRadius: 8,
    backgroundColor: 'rgba(255,255,255,0.12)',
  },
  controlDisabled: { opacity: 0.4 },
  controlText: { color: '#FFFFFF', fontSize: 13, fontWeight: '600' },
});
